import { type ReactNode, type FC, type MouseEventHandler } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { type DefaultProps } from '../common'
import { MenuButton } from '../menu-button/menu-button'

export interface ActionTileProps extends DefaultProps {
  /**
   * Title
   */
  title: string

  /**
   * Icon (Optional)
   */
  icon?: ReactNode

  /**
   * Image (Optional)
   */
  image?: string

  /**
   * Description
   */
  description: string | ReactNode

  /**
   * Click handler
   */
  onClick?: MouseEventHandler<HTMLDivElement>

  /**
   * Disabled state
   */
  disabled?: boolean

  /**
   * Variant - default, account, external-account
   */
  variant?: 'default' | 'account' | 'external-account'

  /**
   * Value
   */
  value?: string | ReactNode
}

export const ActionTile: FC<ActionTileProps> = ({
  title,
  description,
  icon,
  image,
  onClick,
  children,
  testId,
  className,
  id,
  ariaLabel,
  disabled,
  variant = 'default',
  value,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx('rounded-lg  w-full border border-borders-dark', {
    'group hover:border-borders-focus active:bg-gray-50 active:border-borders-focus': variant !== 'external-account',
    'border-borders-light text-text-disabled cursor-not-allowed hover:border-borders-light active:border-borders-light active:bg-[unset]':
      disabled && variant !== 'external-account',
    'p-sm': variant === 'default',
    'p-xs': variant === 'account' || variant === 'external-account',
  })

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  // Helper function to render icon or menu button
  const renderIconOrMenuButton = (): ReactNode => {
    if (icon) return icon
    if (image) return <img src={image} alt={title} className="w-6 h-6 flex-shrink-0 self-start mr-xs" />
    if (variant === 'external-account' && Boolean(onClick)) {
      return (
        <MenuButton
          className="self-start mr-xs"
          vertical
          onClick={onClick as unknown as MouseEventHandler<HTMLButtonElement>}
          inverseColor
          disabled={disabled}
        />
      )
    }
    return null
  }

  const content = (
    <div
      className={cx('flex flex-col gap-3xs', {
        'mb-xs': children && variant === 'default',
      })}
    >
      <div className="inline-flex flex-row items-center w-full">
        {renderIconOrMenuButton()}

        <div
          className={cx('flex flex-col', {
            'gap-3xs': variant === 'default',
          })}
        >
          <p
            className={cx('flex items-center text-left', {
              'text-text-header': !disabled,
              'text-text-disabled': disabled && variant !== 'external-account',
              'typo-primary-md-regular': variant === 'default',
              'typo-primary-base-regular': variant === 'account' || variant === 'external-account',
              'gap-xs': variant === 'external-account' || icon,
            })}
          >
            {title}
          </p>
          <p
            className={cx('typo-primary-sm-light self-start text-left', {
              'text-text-meta': !disabled,
              'text-text-disabled': disabled && variant !== 'external-account',
              'mb-xs': variant === 'external-account' && children,
            })}
          >
            {description}
          </p>
          {children}
        </div>
        {value ? (
          <div className="typo-primary-md-regular text-right text-text-header ml-auto self-start">{value}</div>
        ) : null}
        {variant === 'default' && !value ? (
          <ChevronRightIcon
            className={cx('w-7 h-7 fill-icons-muted flex-shrink-0 self-start ml-auto', {
              'group-hover:fill-icons-full': !disabled,
              'group-hover:fill-icons-muted': disabled,
            })}
          />
        ) : null}
      </div>
    </div>
  )

  return (variant === 'default' || variant === 'account') && Boolean(onClick) ? (
    <button
      type="button"
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      onClick={onClick as unknown as MouseEventHandler<HTMLButtonElement>}
      disabled={disabled}
    >
      {content}
    </button>
  ) : (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {content}
    </div>
  )
}
